import * as React from "react";
import { ZObjState } from "src/types/ZObjState";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";
import { NodeId, ObjectNode, TreeNode } from "../model/node";
import { NodeActions } from "../model/permissions";
import { ZRolesGroup, ZTypeActions } from "../../Obj2Tab/roles/roleTypes";

/**
 * Что обработчик варианта может спросить у окружения.
 *
 * Только чтение готовых данных. Ни дерева целиком, ни стора, ни сети:
 * загрузку обработчик делает сам, а состояние ему не принадлежит.
 */
export type KindContext = {
  node(id: NodeId): TreeNode | undefined;
  ownerObject(id: NodeId): ObjectNode | undefined;
  details(objectId: ZUid): ZObjectItem | undefined;
  /** Состояния объекта. По ним строится таблица прав атрибута. */
  states(objectId: ZUid): ZObjState[] | undefined;
};

/**
 * Поведение одного варианта узла.
 *
 * Заменяет шесть switch-блоков v1: операция — это обращение к таблице,
 * а не ещё одна ветка в каждом из шести мест.
 */
export type KindHandler<N extends TreeNode> = {
  /**
   * Заголовок узла. Права передаются уже вычисленными: замок в заголовке
   * означает действующий запрет на изменение, а он зависит и от владельца.
   */
  title(node: N, actions: NodeActions): React.ReactNode;
  icon(node: N): React.ReactNode;
  /**
   * Собственные права узла. Наследование от владельца выполняет вызывающая
   * сторона: иначе обработчику пришлось бы ходить вверх по дереву.
   */
  ownActions(node: N, ctx: KindContext): NodeActions;
  /**
   * Данные для формы редактирования узла.
   * Есть у каждого варианта: форма открывается для любого выбранного узла,
   * различается только её содержимое.
   */
  formData(node: N, ctx: FormDataContext): Promise<unknown>;
  /**
   * Текст подтверждения удаления.
   * Есть у каждого варианта, включая те, которые удалять нельзя: окно
   * подтверждения открывается по выбранному узлу, а вопрос должен называть
   * то, что пользователь видит.
   */
  removeMessage(node: N): string;
};

/**
 * Окружение для сбора данных формы: к чтению дерева добавляются справочники,
 * без которых не построить права нового атрибута.
 */
export type FormDataContext = KindContext & {
  roleGroups: ZRolesGroup[];
  attrTypeActions: ZTypeActions[];
};

/**
 * Вариант, у которого бывают дети.
 *
 * «Лист» выражается типом: у обработчика атрибута метода `children` нет вовсе.
 * В v1 это же ограничение жило в рантайме — исключением «Нельзя сохранять
 * значение» и молчаливыми ветками, ничего не делающими для листьев.
 */
export type LazyKindHandler<N extends TreeNode> = KindHandler<N> & {
  /**
   * Может ли у узла в принципе появиться потомство.
   * Отвечает до загрузки: группа по справочнику без привязанного атрибута —
   * лист, и стрелку раскрытия ей рисовать не нужно.
   */
  isExpandable(node: N): boolean;
  children(node: N, ctx: KindContext): Promise<TreeNode[]>;
};

export type ChildrenLoader = () => Promise<TreeNode[]>;

/**
 * Чем закончилось сохранение.
 *
 * Структура объекта после записи могла измениться где угодно, поэтому вместо
 * шести частных случаев обновления узлов, как в v1, объект перечитывается
 * целиком.
 *
 * Ответы на запись сюда не попадают намеренно. Ответ на создание объекта - не
 * то же самое, что отдаёт чтение: в нём нет списка прав, и объект выглядел бы
 * запрещённым к изменению сразу после создания.
 */
export type SaveResult = {
  /** Объект, которого коснулось сохранение. */
  objectId: ZUid;
  /** Узел, который должен остаться выбранным. */
  selectId?: NodeId;
};

/**
 * Вариант, который можно сохранить.
 * У значения справочника метода нет: справочники редактируются на своей вкладке,
 * и в v1 попытка сохранения заканчивалась исключением в глубине визитора.
 */
export type SavableKindHandler<N extends TreeNode> = {
  save(node: N, values: unknown, ctx: SaveContext): Promise<SaveResult>;
};

/** Окружение сохранения: к чтению дерева добавляется справочник типов. */
export type SaveContext = KindContext & {
  attrTypesDict: Record<number, string>;
};

/**
 * Вариант, который можно удалить.
 *
 * Значения справочника удалять нельзя — у их обработчика этого метода нет.
 * В v1 запрет жил в рантайме: `throw Error("Нельзя удалить значение")`
 * в глубине визитора, уже после подтверждения пользователем.
 *
 * Текст вопроса живёт в `KindHandler`, а не здесь: спросить можно про любой
 * узел, а выполнить — не про любой.
 */
export type RemovableKindHandler<N extends TreeNode> = {
  remove(node: N, ctx: KindContext): Promise<void>;
};

/**
 * Детали объекта обязаны быть загружены до обращения к детям и правам.
 * Порядок обеспечивает вызывающая сторона, поэтому нарушение — ошибка
 * программиста, а не пользователя: пустой результат её бы замаскировал.
 */
export const requireDetails = (
  ctx: KindContext,
  objectId: ZUid,
): ZObjectItem => {
  const details = ctx.details(objectId);
  if (!details) throw Error(`Детали объекта ${objectId} не загружены`);
  return details;
};
